import { useQuery } from '@tanstack/react-query';
import httpClient from '../api/httpClient';

interface PriceData {
    symbol: string;
    bid: number;
    ask: number;
    last: number;
    provider: string;
    timestamp: number;
}

interface UseCurrentInstrumentPriceReturn {
    price: PriceData | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

// Fetch price for a single instrument (priority fetch for buy/sell buttons)
const fetchCurrentInstrumentPrice = async (instrumentId: string): Promise<PriceData | null> => {
    try {
        if (!instrumentId) {
            return null;
        }

        // Use the single instrument price endpoint for faster response
        const response = await httpClient.get(`/trader/charts/instruments/${instrumentId}/price`);

        if (response.data && response.data.price !== undefined) {
            const midPrice = response.data.price || 0;
            const symbol = response.data.symbol || instrumentId;

            // Calculate realistic spread based on instrument type
            let spreadPips = 0.0002; // Default 2 pips

            // Determine spread based on symbol characteristics
            if (symbol.includes('JPY')) {
                spreadPips = 0.02; // 2 pips for JPY pairs
            } else if (symbol.includes('BTC') || symbol.includes('ETH')) {
                spreadPips = midPrice * 0.0005; // 0.05% for crypto
            } else if (symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('USD')) {
                spreadPips = 0.0002; // 2 pips for major forex pairs
            } else if (symbol.includes('GOLD') || symbol.includes('OIL')) {
                spreadPips = midPrice * 0.0002; // 0.02% for commodities
            } else {
                spreadPips = midPrice * 0.0002; // 0.02% for stocks/others
            }

            // Calculate bid/ask with spread
            const halfSpread = spreadPips / 2;
            const ask = midPrice + halfSpread;
            const bid = Math.max(0, midPrice - halfSpread);

            return {
                symbol: symbol,
                bid: bid,
                ask: ask,
                last: midPrice,
                provider: 'backend',
                timestamp: response.data.timestamp || Date.now(),
            };
        }

        return null;
    } catch (error) {
        console.error('Failed to fetch current instrument price:', error);
        return null;
    }
};

export const useCurrentInstrumentPrice = (
    instrumentId: string,
    _pollingInterval?: number // No longer used, kept for backward compatibility
): UseCurrentInstrumentPriceReturn => {
    const {
        data: price = null,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['current-instrument-price', instrumentId],
        queryFn: () => fetchCurrentInstrumentPrice(instrumentId),
        // No polling — prices are pushed via WebSocket (usePriceSocket)
        staleTime: Infinity,
        gcTime: 2 * 60 * 1000,
        retry: 2,
        retryDelay: 500,
        enabled: !!instrumentId,
    });

    return {
        price,
        isLoading,
        error: error?.message || null,
        refetch,
    };
};
